import {isImmutable} from 'immutable';
import * as t from 'io-ts';
import {isEqual, mapValues, ObjectIterator} from 'lodash';

import {isDevelopment} from '../../../../helpers/environment/envHelpers';
import {
  checkStashVersionIncludesVersion,
  mergeStashVersions,
  RStashVersion,
  StashSourcesEnum,
  StashVersion,
  zeroStashVersion,
} from './stashVersion';

/*
 * Types.
 */

interface StashVersionedValue<T> {
  /** A value. */
  readonly value: T;
  /** The version of the value. */
  readonly version: StashVersion;
}

export interface StashItem<T> extends StashVersionedValue<T> {
  /** The version that has been synchronized to the server. */
  readonly serverVersion: StashVersion;
}

export interface StashServerItem<T> {
  /** A value. */
  readonly value: T;
  /** The server's version of the value. */
  readonly version: number;
}

/*
 * Helpers.
 */

export function makeStashItems<T extends {[key: string]: any}>(values: T) {
  const makeItem: ObjectIterator<T, StashItem<T[keyof T]>> = (value) => ({
    value,
    version: zeroStashVersion,
    serverVersion: zeroStashVersion,
  });

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return mapValues(values, makeItem) as {
    [K in keyof T]: StashItem<T[K]>;
  };
}

export function makeStashItemType<T extends t.Mixed>(valueType: T): t.Type<StashItem<t.TypeOf<T>>> {
  return t.type({
    value: valueType,
    version: RStashVersion,
    serverVersion: RStashVersion,
  });
}

export function makeStashItemTypes<T extends {[key: string]: t.Mixed}>(valueTypes: T) {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return mapValues(valueTypes, makeStashItemType) as {
    [K in keyof T]: t.Type<StashItem<t.TypeOf<T[K]>>>;
  };
}

export function makeStashServerItemTypes<T extends {[key: string]: t.Mixed}>(valueTypes: T) {
  const makeItemType: ObjectIterator<T, t.Type<StashServerItem<t.TypeOf<T[keyof T]>>>> = (valueType) =>
    t.type({
      value: valueType,
      version: t.number,
    });

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return mapValues(valueTypes, makeItemType) as {
    [K in keyof T]: t.Type<StashServerItem<t.TypeOf<T[K]>>>;
  };
}

export function makeStashItemMergeStrategies<T extends {[key: string]: any}>(valueMergeStrategies: {
  [K in keyof T]: ((left: T[K], right: T[K]) => T[K]) | undefined;
}) {
  const makeItemMergeStrategy: ObjectIterator<
    typeof valueMergeStrategies,
    <V extends T[keyof T]>(left: StashItem<V>, right: StashItem<V>) => StashItem<V>
  > = (valueMergeStrategy, key) => (left, right) => {
    try {
      return mergeStashItem(left, right, valueMergeStrategy);
    } catch (error) {
      throw new Error(`Error merging stash item for key "${key}": ${error.message}`);
    }
  };

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return mapValues(valueMergeStrategies, makeItemMergeStrategy) as {
    [K in keyof T]: <V extends T[K]>(left: StashItem<V>, right: StashItem<V>) => StashItem<V>;
  };
}

export function mergeStashItem<T>(
  left: StashItem<T>,
  right: StashItem<T>,
  mergeValue: ((left: T, right: T) => T) | undefined,
): StashItem<T> {
  return {
    // Merge our value.
    ...mergeStashVersionedValue(left, right, mergeValue),
    // Merge our knowledge of what has been synchronized to the server.
    serverVersion: mergeStashVersions(left.serverVersion, right.serverVersion),
  };
}

function mergeStashVersionedValue<T>(
  left: StashVersionedValue<T>,
  right: StashVersionedValue<T>,
  mergeValue: ((left: T, right: T) => T) | undefined,
): StashVersionedValue<T> {
  // In development, enforce the merge function is idempotent, commutative, and associative.
  if (isDevelopment() && mergeValue) {
    assertMergeConverges(mergeValue, left.value, right.value);
  }

  // The left value is at least as new as the right value.
  if (checkStashVersionIncludesVersion(left.version, right.version)) {
    return left;
  }

  // The right value is at least as new as the left value.
  if (checkStashVersionIncludesVersion(right.version, left.version)) {
    return right;
  }

  // The values were updated concurrently, so we need to merge them. Fall back to the value most recently
  // updated by the client.
  const fallbackValue =
    left.version[StashSourcesEnum.CLIENT] >= right.version[StashSourcesEnum.CLIENT]
      ? left.value
      : right.value;

  return {
    value: mergeValue ? mergeValue(left.value, right.value) : fallbackValue,
    version: mergeStashVersions(left.version, right.version),
  };
}

/** Enforces the merge function is idempotent, commutative, and associative for these values. */
export function assertMergeConverges<T>(mergeValue: (left: T, right: T) => T, left: T, right: T): void {
  // Assert the function is idempotent.
  if (!isImmutableEqual(mergeValue(left, left), left)) {
    throw new Error('The merge strategy function is not idempotent.');
  }
  if (!isImmutableEqual(mergeValue(right, right), right)) {
    throw new Error('The merge strategy function is not idempotent.');
  }

  // Assert the function is commutative.
  if (!isImmutableEqual(mergeValue(left, right), mergeValue(right, left))) {
    throw new Error('The merge strategy function is not commutative.');
  }

  // Assert the function is associative.
  if (
    !isImmutableEqual(mergeValue(mergeValue(left, left), right), mergeValue(left, mergeValue(left, right)))
  ) {
    throw new Error('The merge strategy function is not associative.');
  }
  if (
    !isImmutableEqual(mergeValue(mergeValue(right, right), left), mergeValue(right, mergeValue(right, left)))
  ) {
    throw new Error('The merge strategy function is not associative.');
  }
}

function isImmutableEqual<T>(left: T, right: T): boolean {
  if (isImmutable(left)) {
    return left.equals(right);
  }

  return isEqual(left, right);
}
